import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FolderOpen, Download, Pencil, Trash2, FolderPlus, Eye, History, FolderInput } from 'lucide-react';
import { useLanguage } from '../../i18n';

const FileManagerContextMenu = ({
    x,
    y,
    type,
    isProtected = false,
    onClose,
    onOpen,
    onRename,
    onDelete,
    onDownload,
    onNewFolder,
    onVersionHistory,
    onMove
}) => {
    const { t } = useLanguage();
    const menuRef = useRef(null);

    // Adjust position if menu would go off-screen
    useEffect(() => {
        if (menuRef.current) {
            const rect = menuRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            let adjustedX = x;
            let adjustedY = y;

            if (x + rect.width > viewportWidth) {
                adjustedX = viewportWidth - rect.width - 10;
            }

            if (y + rect.height > viewportHeight) {
                adjustedY = viewportHeight - rect.height - 10;
            }

            menuRef.current.style.left = `${adjustedX}px`;
            menuRef.current.style.top = `${adjustedY}px`;
        }
    }, [x, y]);

    // Close on escape
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    const MenuItem = ({ icon: Icon, label, onClick, danger = false, disabled = false, title, testId }) => (
        <button
            onClick={disabled ? undefined : onClick}
            disabled={disabled}
            title={title}
            data-testid={testId}
            className={`
                w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-left transition-colors
                ${disabled
                    ? 'text-white/30 cursor-not-allowed'
                    : danger
                        ? 'text-pai-red hover:bg-pai-red/[0.12]'
                        : 'text-pai-body hover:bg-white/[0.07] hover:text-white'
                }
            `}
        >
            <Icon size={16} />
            <span>{label}</span>
        </button>
    );

    const Divider = () => (
        <div className="my-1.5 border-t border-pai-hairline" />
    );

    // Determine what menu items to show
    const isFolder = type === 'folder';
    const isDocument = type === 'document';
    const isEmptySpace = !type;

    // Render via a portal to document.body so the menu's `position: fixed`
    // is anchored to the viewport regardless of any transformed ancestor
    // (e.g. the panel's slide-in animation leaves `transform: translateX(0)`
    // applied — which otherwise turns the panel into the containing block
    // for any fixed descendant, throwing the click coordinates off).
    const menu = (
        <div
            ref={menuRef}
            className="fixed z-[70] bg-pai-modal rounded-card shadow-modal-sm border border-pai py-1.5 min-w-[190px] overflow-hidden"
            style={{ left: x, top: y }}
            onClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.preventDefault()}
        >
            {/* Empty space menu */}
            {isEmptySpace && (
                <>
                    <MenuItem icon={FolderPlus} label={t('fmContextNewFolder', 'New Folder')} onClick={onNewFolder} />
                </>
            )}

            {/* Folder menu */}
            {isFolder && (
                <>
                    <MenuItem icon={FolderOpen} label={t('commonOpen', 'Open')} onClick={onOpen} />
                    <Divider />
                    {/* Move (#394): the same item, in the same slot (before Rename), as
                        the document menu — the explicit, menu-driven way to nest a
                        folder, alongside dragging it. A protected /Financials folder
                        stays in its tree, so Move is shown DISABLED with the reason
                        (exactly how Delete is treated below). */}
                    <MenuItem
                        icon={FolderInput}
                        label={t('fmContextMove', 'Move')}
                        onClick={onMove}
                        testId="fm-context-move"
                        disabled={isProtected}
                        title={isProtected
                            ? t('fmProtectedFolderCannotMove', "System financial folders can't be moved. You can rename them; they stay in the Financials tree.")
                            : undefined}
                    />
                    <MenuItem icon={Pencil} label={t('commonRename', 'Rename')} onClick={onRename} />
                    {/* Financial Folders (#145): a protected /Financials folder can be
                        renamed but NOT deleted — the Delete item is shown DISABLED with
                        an explanatory tooltip (the "notification" on attempt). */}
                    <MenuItem
                        icon={Trash2}
                        label={t('commonDelete', 'Delete')}
                        onClick={onDelete}
                        testId="fm-context-delete"
                        danger
                        disabled={isProtected}
                        title={isProtected
                            ? t('fmProtectedFolderCannotDelete', "System financial folders can't be deleted. You can rename them, and delete the files inside.")
                            : undefined}
                    />
                </>
            )}

            {/* Document menu */}
            {isDocument && (
                <>
                    <MenuItem icon={Eye} label={t('commonPreview', 'Preview')} onClick={onOpen} />
                    <MenuItem icon={Download} label={t('commonDownload', 'Download')} onClick={onDownload} />
                    <MenuItem icon={History} label={t('fmVersionHistory', 'Version History')} onClick={onVersionHistory} />
                    <Divider />
                    {/* Move folders (#255): the PO mandates this sits BETWEEN
                        Version History and Rename. It opens the destination
                        picker; nothing moves until the dialog is confirmed. */}
                    <MenuItem icon={FolderInput} label={t('fmContextMove', 'Move')} onClick={onMove} testId="fm-context-move" />
                    <MenuItem icon={Pencil} label={t('commonRename', 'Rename')} onClick={onRename} />
                    <MenuItem icon={Trash2} label={t('commonDelete', 'Delete')} onClick={onDelete} danger testId="fm-context-delete" />
                </>
            )}
        </div>
    );

    return createPortal(menu, document.body);
};

export default FileManagerContextMenu;
